/**
 * Engine-side validation constants mirrored from
 * `cloud-connector-v3/src/core/alarm_engine.py`. Kept here so the SPA's
 * client-side validators match what the edge will accept; drift would
 * surface as "the form passes but the edge rejects" — a bad UX.
 *
 * Update both files together when changing a bound.
 */

export const SUPPORTED_SCHEMA_VERSIONS = [1, 2, 3, 4] as const;
export type SchemaVersion = (typeof SUPPORTED_SCHEMA_VERSIONS)[number];

/**
 * v4 — per-rule notification + auto-clear + propagate fields. v3 payloads
 * remain valid (the edge defaults `auto_clear:true`, `propagate:"TENANT"`,
 * `notification:null` so existing rules continue to run without emails).
 */
export const CURRENT_SCHEMA_VERSION: SchemaVersion = 4;

/** ThingsBoard alarm propagation levels. */
export const VALID_PROPAGATE_LEVELS = [
  "NONE",
  "OWNER",
  "OWNER_HIERARCHY",
  "TENANT",
] as const;
export type PropagateLevel = (typeof VALID_PROPAGATE_LEVELS)[number];

/**
 * Inclusive upper bound on the v4 escalation `after_h` field — matches
 * the edge's :data:`MAX_ESCALATE_AFTER_H` (30 days × 24 h). Fractional
 * hours are accepted so e2e tests can use ~0.05 h for short windows.
 */
export const MAX_ESCALATE_AFTER_H = 720;

/** Cap on `duration_s` fields (24 h). */
export const MAX_DURATION_S = 60 * 60 * 24;

/** Cap on window-style fields (30 days). */
export const MAX_WINDOW_S = 60 * 60 * 24 * 30;

export const VALID_NUMERIC_OPERATORS = [
  ">",
  ">=",
  "<",
  "<=",
  "==",
  "!=",
] as const;
export type NumericOperator = (typeof VALID_NUMERIC_OPERATORS)[number];

export const VALID_SEVERITIES = [
  "CRITICAL",
  "MAJOR",
  "MINOR",
  "WARNING",
  "INDETERMINATE",
] as const;
export type Severity = (typeof VALID_SEVERITIES)[number];

export const VALID_DELIVERY_METHODS = ["notification", "email"] as const;
export type DeliveryMethod = (typeof VALID_DELIVERY_METHODS)[number];

export const VALID_RATE_DIRECTIONS = ["increase", "decrease", "both"] as const;
export type RateDirection = (typeof VALID_RATE_DIRECTIONS)[number];

/** Aggregation operators recognised on v3 rolling-window datapoints. */
export const VALID_AGG_OPERATORS = [
  "avg",
  "sum",
  "min",
  "max",
  "count",
  "stddev",
  "delta",
  "last",
  "first",
] as const;
export type AggOperator = (typeof VALID_AGG_OPERATORS)[number];

/** Composite-rule logical operators. */
export const VALID_COMPOSITE_OPERATORS = ["and", "or"] as const;
export type CompositeOperator = (typeof VALID_COMPOSITE_OPERATORS)[number];

/** Composite-rule treatment of missing children. */
export const VALID_COMPOSITE_MISSING_MODES = ["false", "unknown"] as const;
export type CompositeMissingMode =
  (typeof VALID_COMPOSITE_MISSING_MODES)[number];

/** The 14 rule types the engine knows about (12 v2 + 2 v3). */
export const VALID_RULE_TYPES = [
  "threshold",
  "range",
  "boolean",
  "inactivity",
  "stuck",
  "rate_of_change",
  "delta",
  "daily_energy",
  "excessive_cycling",
  "peak_demand",
  "idle_consumption",
  "expression",
  "composite",
  "comm_degradation",
] as const;
export type RuleType = (typeof VALID_RULE_TYPES)[number];

/** Rule types that don't bind a single `datapoint` topic. */
export const RULE_TYPES_WITHOUT_DATAPOINT: ReadonlySet<RuleType> = new Set([
  "expression",
  "composite",
]);

/** v3 `expression` function-call whitelist. */
export const ALLOWED_EXPRESSION_CALLS = ["min", "max", "abs", "len"] as const;
export type ExpressionCall = (typeof ALLOWED_EXPRESSION_CALLS)[number];

export const MAX_EXPRESSION_CALL_ARGS = 16;
export const MAX_EXPRESSION_CALL_DEPTH = 2;
export const MAX_EXPRESSION_LENGTH = 2000;

/** Cap on simultaneous BESS devices that can be picked in one fan-out. */
export const MAX_FANOUT_DEVICES = 16;

/** SHARED_SCOPE attribute key for the rule payload. */
export const RULES_ATTRIBUTE_KEY = "bess_alarm_rules";
/** SERVER_SCOPE attribute key for the edge ACK. */
export const RULES_STATUS_ATTRIBUTE_KEY = "bess_alarm_rules_status";
/** SERVER_SCOPE attribute key for the per-device datapoint catalog. */
export const AVAILABLE_DATAPOINTS_ATTRIBUTE_KEY = "available_datapoints";
